import { PropsWithChildren } from 'react';
import {
    Modal as AriaModal,
    Dialog,
    DialogTrigger,
    Heading,
    ModalOverlay,
} from 'react-aria-components';
import styled, { createGlobalStyle } from 'styled-components';
import { Box } from '../atoms/Box';
import Icon from '../atoms/icons/Icon';
import { StyledText } from '../atoms/Texts';
import { Button } from '../molecules';

const StyledModalOverlay = styled(ModalOverlay)`
    background: rgba(0, 0, 0, 0.4);
`;

export const Modal = (
    props: PropsWithChildren<
        React.ComponentProps<typeof AriaModal> & {
            heading: string;
        }
    >,
) => {
    const { heading, children, ...ariaModalProps } = props;
    return (
        <>
            <ModalStyles />
            <DialogTrigger>
                <AriaModal {...ariaModalProps} isDismissable>
                    <StyledModalOverlay />
                    <Dialog>
                        <Heading hidden>{heading}</Heading>
                        <Box y="32" x="32">
                            {children}
                        </Box>
                    </Dialog>
                </AriaModal>
            </DialogTrigger>
        </>
    );
};

// Needs to at least >99999 because of some customers
export const zIndexModal = 999999;

const ModalStyles = createGlobalStyle`


  .react-aria-Modal {
    background-color: white;

    border-radius: 6px;
    outline: none;
    max-width: calc(100% - 20px);
    max-height: calc(100% - 20px);

    &[data-entering] {
      animation: modal-zoom 400ms cubic-bezier(0.175, 0.885, 0.32, 1.275);
    }
  }

  @keyframes modal-fade {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes modal-zoom {
    from {
      transform: scale(0.8);
    }
    to {
      transform: scale(1);
    }
  }
`;

export function ModalWithContent({
    isModalOpen,
    onModalClose,
    title,
    content,
    children,
    ariaLabelForCancel,
}: PropsWithChildren<{
    isModalOpen: boolean;
    onModalClose: (newValue: boolean) => unknown;
    title: string;
    content: string | React.ReactNode;
    ariaLabelForCancel?: string;
}>) {
    return (
        <Modal heading={title} isOpen={isModalOpen} onOpenChange={onModalClose}>
            <FlexboxMinWidth gap="12" direction="column">
                <Box direction="column" gap="16">
                    <Box
                        alignItems="center"
                        justifyContent="space-between"
                        direction="row"
                        wrap="nowrap"
                    >
                        <StyledText variant="h3" as="h1">
                            {title}
                        </StyledText>

                        <Button
                            ariaLabel={ariaLabelForCancel ?? 'close'}
                            onClick={() => onModalClose(false)}
                            variant="ghost"
                        >
                            <Icon name="exit" />
                        </Button>
                    </Box>
                    <Box direction="column" gap="4">
                        <BreakOnWhiteSpace variant="body">
                            {content}
                        </BreakOnWhiteSpace>
                    </Box>
                </Box>
                {children}
            </FlexboxMinWidth>
        </Modal>
    );
}

const BreakOnWhiteSpace = styled(StyledText)`
    white-space: pre-line;
`;

const FlexboxMinWidth = styled(Box)`
    min-width: 400px;
    max-width: 600px;
`;
